import { euEtsCountriesSet } from '../../shipment/EU_ETS_countries';

/**  Global Warming Potential (GWP) factors */
export const GWP = {
  /** GWP factor for Carbon dioxide */
  CO2: 1,
  /** GWP factor for Methane */
  CH4: 28,
  /** GWP factor for Nitrous oxide */
  N2O: 265,
};

/** FuelEU compliance price in EUR per equivalent metric ton of VLSF */
export const FUEL_EU_COMPLIANCE_PRICE_EUR = 2400;
const VLSFO_ENERGY_MJ_PER_TONNE = 41_000;

export interface FuelEmissionFactors {
  /** Pathway name from Fuel EU regulation */
  name: string;
  id: string;
  /** Lower Calorific Value in MJ/g */
  lcv: number;
  /** Well-to-Tank emission factor in gCO2eq/MJ */
  wtTEmissionFactor: number;
  /** Tank-to-Wake CO2 emission factor in gCO2/gFuel */
  co2EmissionFactor: number;
  /** Tank-to-Wake CH4 emission factor in gCH4/gFuel */
  ch4EmissionFactor: number;
  /** Tank-to-Wake N2O emission factor in gN2O/gFuel */
  n2oEmissionFactor: number;
}

export const fuelEuGrades = [
  {
    name: 'HFO ISO 8217 Grades RME to RMK',
    id: 'hfo_iso_8217_grades_rme_to_rmk',
    lcv: 0.0405,
    wtTEmissionFactor: 13.5,
    co2EmissionFactor: 3.114,
    ch4EmissionFactor: 0.00005,
    n2oEmissionFactor: 0.00018,
  },
  {
    name: 'LFO ISO 8217 Grades RMA to RMD',
    id: 'lfo_iso_8217_grades_rma_to_rmd',
    lcv: 0.041,
    wtTEmissionFactor: 13.2,
    co2EmissionFactor: 3.151,
    ch4EmissionFactor: 0.00005,
    n2oEmissionFactor: 0.00018,
  },
  {
    name: 'MDO/MGO ISO 8217 Grades DMX to DMB',
    id: 'mdo_mgo_iso_8217_grades_dmx_to_dmb',
    lcv: 0.0427,
    wtTEmissionFactor: 14.4,
    co2EmissionFactor: 3.206,
    ch4EmissionFactor: 0.00005,
    n2oEmissionFactor: 0.00018,
  },
  {
    name: 'LNG',
    id: 'lng',
    lcv: 0.0491,
    wtTEmissionFactor: 18.5,
    co2EmissionFactor: 2.75,
    ch4EmissionFactor: 0,
    n2oEmissionFactor: 0.00011,
  },
  {
    name: 'e-diesel',
    id: 'e_diesel',
    lcv: 0.0427,
    wtTEmissionFactor: 0, // Source: Directive (EU) 2018/2001
    co2EmissionFactor: 3.206,
    ch4EmissionFactor: 0.00005,
    n2oEmissionFactor: 0.00018,
  },
  {
    name: 'e-methanol',
    id: 'e_methanol',
    lcv: 0.0199,
    wtTEmissionFactor: 0, // Source: Directive (EU) 2018/2001
    co2EmissionFactor: 1.375,
    ch4EmissionFactor: 0, // TBM (To Be Measured)
    n2oEmissionFactor: 0, // TBM (To Be Measured)
  },
  {
    name: 'e-LNG',
    id: 'e_lng',
    lcv: 0.0491,
    wtTEmissionFactor: 0, // Source: Directive (EU) 2018/2001
    co2EmissionFactor: 2.75,
    ch4EmissionFactor: 0,
    n2oEmissionFactor: 0.00011,
  },
] as const satisfies FuelEmissionFactors[];

// Separate type from FuelEmissionFactors so we get better type inference
type FuelEuGrade = (typeof fuelEuGrades)[number];
type FuelEuGradeId = FuelEuGrade['id'];
type FuelEuGradeById = Record<FuelEuGradeId, FuelEuGrade>;

export const fuelEuGradeById = fuelEuGrades.reduce((acc, grade) => {
  return {
    ...acc,
    [grade.id]: grade,
  };
}, {} as FuelEuGradeById);

const METRIC_TONNE_IN_GRAMS = 1_000_000;

export interface FuelEuEmissions {
  /** Methane emissions in gCO2eq */
  ch4Emissions: number;
  /** Carbon dioxide emissions in gCO2 */
  co2Emissions: number;
  /** Energy used in MJ */
  energyMJ: number;
  /** Nitrogen emissions in gCO2eq */
  n2oEmissions: number;
  /** Emissions in gCO2eq */
  tankToWakeEmissions: number;
  /** Emissions in gCO2eq */
  wellToTankEmissions: number;
}

/**
 * Calculate FuelEU emissions for a given consumption
 */
export function calculateFuelEuEmissions(
  consumedBunkerTonnes: number,
  emissionFactors: {
    carbonFactor: number;
    ch4Factor: number;
    lcv: number;
    n2oFactor: number;
    wttFactor: number;
  },
): FuelEuEmissions {
  // Calculate energy in MJ
  const energyMJ =
    consumedBunkerTonnes * METRIC_TONNE_IN_GRAMS * emissionFactors.lcv;

  // Calculate Well-to-Tank emissions in gCO2eq
  const wellToTankEmissions = energyMJ * emissionFactors.wttFactor;

  // Calculate Tank-to-Wake emissions in gCO2eq
  const co2Emissions =
    consumedBunkerTonnes *
    METRIC_TONNE_IN_GRAMS *
    emissionFactors.carbonFactor *
    GWP.CO2;
  const ch4Emissions =
    consumedBunkerTonnes *
    METRIC_TONNE_IN_GRAMS *
    emissionFactors.ch4Factor *
    GWP.CH4;
  const n2oEmissions =
    consumedBunkerTonnes *
    METRIC_TONNE_IN_GRAMS *
    emissionFactors.n2oFactor *
    GWP.N2O;
  const tankToWakeEmissions = co2Emissions + ch4Emissions + n2oEmissions;

  return {
    ch4Emissions,
    co2Emissions,
    energyMJ,
    n2oEmissions,
    tankToWakeEmissions,
    wellToTankEmissions,
  };
}

export interface FuelEuEmissionResult {
  /** Amount of energy used that is applicable on Fuel EU calculations */
  effectiveTotalEnergyMJ: number;
  /** Tank-to-Wake emissions scaled by country and vessel factor in gCO2eq */
  effectiveTotalTankToWakeEmissions: number;
  /** Well-to-Tank emissions scaled by country and vessel factor in gCO2eq */
  effectiveTotalWellToTankEmissions: number;
  /** Total energy in MJ without country or vessel factor */
  totalEnergyMJ: number;
  /** Total Well-to-Tank emissions in gCO2eq without country or vessel factor */
  totalWellToTankEmissions: number;
  /** Total Tank-to-Wake emissions in gCO2eq without country or vessel factor */
  totalTankToWakeEmissions: number;
  /** GHG intensity in gCO2eq/MJ */
  ghgIntensity: number;
}

/**
 * Calculate FuelEU GHG intensity and total emissions
 */
export function getFuelEuTotals(
  allEmissions: FuelEuEmissions[],
  fuelEuFactor: number,
): FuelEuEmissionResult {
  let totalEnergyMJ = 0;
  let totalWellToTankEmissions = 0;
  let totalTankToWakeEmissions = 0;

  for (const emissions of allEmissions) {
    totalEnergyMJ += emissions.energyMJ;
    totalWellToTankEmissions += emissions.wellToTankEmissions;
    totalTankToWakeEmissions += emissions.tankToWakeEmissions;
  }

  const effectiveTotalEnergyMJ = totalEnergyMJ * fuelEuFactor;
  const effectiveTotalWellToTankEmissions =
    totalWellToTankEmissions * fuelEuFactor;
  const effectiveTotalTankToWakeEmissions =
    totalTankToWakeEmissions * fuelEuFactor;

  const ghgIntensity = calculateGhgIntensity(
    effectiveTotalEnergyMJ,
    effectiveTotalWellToTankEmissions,
    effectiveTotalTankToWakeEmissions,
  );

  return {
    effectiveTotalEnergyMJ,
    effectiveTotalTankToWakeEmissions,
    effectiveTotalWellToTankEmissions,
    totalEnergyMJ,
    totalWellToTankEmissions,
    totalTankToWakeEmissions,
    ghgIntensity,
  };
}

/** Calculate GHG intensity in gCO2eq/MJ */
export function calculateGhgIntensity(
  totalEnergyMJ: number,
  totalWellToTankEmissions: number,
  totalTankToWakeEmissions: number,
) {
  return totalEnergyMJ > 0
    ? (totalWellToTankEmissions + totalTankToWakeEmissions) / totalEnergyMJ
    : 0;
}

export function calculateFuelEuComplianceBalance(
  /** GHG Intensity for the shipment */
  ghgIntensity: number,
  /** Start year of the shipment */
  startYear: number,
) {
  const targetGhgIntensity = getTargetGhgIntensity(startYear);

  return {
    targetGhgIntensity,
    complianceBalance: targetGhgIntensity - ghgIntensity,
  };
}

export function calculateFuelEuCompensationCost(
  complianceBalance: number,
  ghgIntensity: number,
  totalEnergyUsedMJ: number,
  /** Price per equivalent metric ton of VLSFO in org base currency */
  compliancePrice: number,
) {
  // Use absolute value of the compliance balance when it's negative
  const effectiveComplianceBalance =
    complianceBalance < 0 ? Math.abs(complianceBalance) : 0;

  if (ghgIntensity <= 0 || totalEnergyUsedMJ <= 0) {
    return 0;
  }

  return (
    (effectiveComplianceBalance * totalEnergyUsedMJ * compliancePrice) /
    (ghgIntensity * VLSFO_ENERGY_MJ_PER_TONNE)
  );
}

/**
 * Calculates the target GHG intensity for shipment based on the year number.
 * @param year The year for which to calculate the target GHG intensity.
 * @returns The target GHG intensity in gCO2eq/MJ.
 */
function getTargetGhgIntensity(year: number): number {
  // Reference value in grams of CO2 equivalent per MJ
  const referenceValue = 91.16;

  // Reduction percentages by year threshold
  let reductionPercentage = 0;

  if (year >= 2050) {
    reductionPercentage = 80;
  } else if (year >= 2045) {
    reductionPercentage = 62;
  } else if (year >= 2040) {
    reductionPercentage = 31;
  } else if (year >= 2035) {
    reductionPercentage = 14.5;
  } else if (year >= 2030) {
    reductionPercentage = 6;
  } else if (year >= 2025) {
    reductionPercentage = 2;
  }

  // Calculate the target value by applying the reduction
  const targetValue = referenceValue * (1 - reductionPercentage / 100);

  // Return the result rounded to 2 decimal places
  return Math.round(targetValue * 100) / 100;
}

export function getFuelEuCountryFactor(countries: string[]) {
  const euCountries = countries.filter((c) => euEtsCountriesSet.has(c));

  if (euCountries.length === countries.length) return 1;
  if (euCountries.length > 0) return 0.5;
  return 0;
}

export function checkVesselMeetsFuelEuCriteria({
  grossTonnage,
}: {
  grossTonnage?: string | number | null;
}): boolean {
  if (!grossTonnage) return false;
  const num = Number(grossTonnage);

  return !isNaN(num) && num > 5000;
}
